import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Share2, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

// Helper function to extract social media URLs from text
const extractSocialUrl = (text) => {
  if (!text) return null;
  
  // Patterns for social media URLs - order matters, more specific first
  const patterns = [
    // TikTok patterns
    /https?:\/\/(?:www\.)?tiktok\.com\/@[\w.-]+\/video\/\d+[^\s]*/i,
    /https?:\/\/(?:www\.)?tiktok\.com\/t\/\w+[^\s]*/i,
    /https?:\/\/vm\.tiktok\.com\/\w+[^\s]*/i,
    // YouTube patterns
    /https?:\/\/(?:www\.)?youtube\.com\/(?:watch\?v=|shorts\/|live\/)[^\s]+/i,
    /https?:\/\/youtu\.be\/[^\s]+/i,
    // Instagram patterns
    /https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|reels)\/[^\s]+/i,
    // Twitter/X patterns
    /https?:\/\/(?:www\.)?(?:twitter\.com|x\.com)\/\w+\/status\/\d+[^\s]*/i,
    // Facebook patterns
    /https?:\/\/(?:www\.)?(?:facebook\.com|fb\.watch)\/[^\s]+/i,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      // Clean up any trailing punctuation or special chars
      return match[0].replace(/[)\]}>.,;:!?'"]+$/, '');
    }
  }
  
  return null;
};

/**
 * ShareTargetPage - Handles incoming shares from other apps via PWA Share Target
 * 
 * When a user shares content from TikTok, Instagram, YouTube, etc. to Par Seeker,
 * this page receives the shared data and redirects to create a new post.
 */
const ShareTargetPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, no_url, error
  const [sharedContent, setSharedContent] = useState(null);

  useEffect(() => {
    // Extract shared content from URL params
    const title = searchParams.get('title') || '';
    const text = searchParams.get('text') || '';
    const url = searchParams.get('url') || '';

    console.log('[ShareTarget] Received:', { title, text, url });

    // Try to find a social media URL in any of the shared fields
    // Priority: url param > extract from text > extract from title
    let extractedUrl = url || extractSocialUrl(text) || extractSocialUrl(title);
    
    // Build the final content for the post
    let content = '';
    
    if (extractedUrl) {
      // We found a social URL - this is the main content
      content = extractedUrl;
      
      // Optionally add a caption from title (if it's not just the URL)
      if (title && !title.includes('tiktok.com') && !title.includes('youtube.com') && 
          !title.includes('instagram.com') && title !== extractedUrl) {
        // Title might be a good caption, add it before the URL
        content = title + '\n\n' + extractedUrl;
      }
    } else if (text) {
      // No URL found, just use the text
      content = text;
      if (title && title !== text && !text.includes(title)) {
        content = title + '\n\n' + text;
      }
    } else if (title) {
      content = title;
    }

    console.log('[ShareTarget] Extracted URL:', extractedUrl);
    console.log('[ShareTarget] Final content:', content);

    // Detect if this looks like TikTok shared content without a URL
    const looksLikeTikTok = !extractedUrl && (
      text.includes('#') || 
      text.includes('♫') || 
      text.includes('@') ||
      text.includes('original sound')
    );

    setSharedContent({
      title,
      text,
      url,
      extractedUrl,
      combined: content.trim(),
      looksLikeTikTok
    });

    // Small delay for visual feedback, then redirect
    const timer = setTimeout(() => {
      if (content.trim()) {
        // If we have content but no URL and it looks like TikTok, show warning
        if (looksLikeTikTok) {
          setStatus('no_url');
        } else {
          setStatus('success');
          // Redirect to social page with the shared content
          const encodedContent = encodeURIComponent(content.trim());
          setTimeout(() => {
            navigate(`/social?share=${encodedContent}`, { replace: true });
          }, 500);
        }
      } else {
        setStatus('error');
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [searchParams, navigate]);

  // If user is not logged in, redirect to login with return URL
  useEffect(() => {
    if (!user) {
      const currentUrl = window.location.href;
      navigate(`/login?redirect=${encodeURIComponent(currentUrl)}`, { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-[var(--ps-bg-surface)] rounded-2xl p-8 text-center border border-[var(--ps-border)]">
          {/* Icon */}
          <div className="mb-6">
            {status === 'processing' && (
              <div className="w-20 h-20 mx-auto rounded-full bg-[var(--ps-primary)]/20 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-[var(--ps-primary)] animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            )}
            {status === 'no_url' && (
              <div className="w-20 h-20 mx-auto rounded-full bg-yellow-500/20 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-yellow-500" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
            )}
          </div>

          {/* Status Message */}
          <h1 className="text-2xl font-bold text-[var(--ps-text-primary)] mb-2">
            {status === 'processing' && 'Processing Share...'}
            {status === 'success' && 'Got it!'}
            {status === 'no_url' && 'Video Link Missing'}
            {status === 'error' && 'Nothing to Share'}
          </h1>
          
          <p className="text-[var(--ps-text-muted)] mb-6">
            {status === 'processing' && 'Preparing your content for posting...'}
            {status === 'success' && 'Opening post editor...'}
            {status === 'no_url' && "TikTok didn't include the video link. Use 'Copy Link' instead to share videos."}
            {status === 'error' && "We couldn't find any content to share. Try copying the link manually."}
          </p>

          {/* No URL - show instructions */}
          {status === 'no_url' && (
            <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-4 text-left mb-4">
              <p className="text-sm font-medium text-[var(--ps-text-primary)] mb-2">To share TikTok videos:</p>
              <ol className="text-sm text-[var(--ps-text-muted)] space-y-1 list-decimal list-inside">
                <li>Open the TikTok video</li>
                <li>Tap the Share arrow</li>
                <li>Select <span className="text-[var(--ps-text-primary)] font-medium">&ldquo;Copy Link&rdquo;</span></li>
                <li>Open Par Seeker and paste the link</li>
              </ol>
            </div>
          )}

          {/* Show what was shared (for debugging/transparency) */}
          {sharedContent && (status === 'success' || status === 'no_url') && (
            <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-4 text-left">
              <p className="text-xs text-[var(--ps-text-muted)] mb-2">
                {status === 'no_url' ? 'Received (no video link):' : 'Sharing:'}
              </p>
              <p className="text-sm text-[var(--ps-text-primary)] break-all line-clamp-3">
                {sharedContent.combined || sharedContent.url || sharedContent.text || 'No content'}
              </p>
            </div>
          )}

          {/* Actions */}
          {status === 'no_url' && (
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => navigate('/social?action=post')}
                className="flex-1 px-4 py-3 bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] rounded-xl font-medium hover:bg-[var(--ps-bg-hover)] transition-colors border border-[var(--ps-border)]"
              >
                Post Anyway
              </button>
              <button
                onClick={() => {
                  const encodedContent = encodeURIComponent(sharedContent.combined);
                  navigate(`/social?share=${encodedContent}`, { replace: true });
                }}
                className="flex-1 px-4 py-3 bg-[var(--ps-primary)] text-white rounded-xl font-medium hover:bg-[var(--ps-primary-hover)] transition-colors"
              >
                Add Link & Post
              </button>
            </div>
          )}

          {/* Error action */}
          {status === 'error' && (
            <button
              onClick={() => navigate('/social?action=post')}
              className="mt-4 px-6 py-3 bg-[var(--ps-primary)] text-white rounded-xl font-medium hover:bg-[var(--ps-primary-hover)] transition-colors"
            >
              Create Post Manually
            </button>
          )}
        </div>

        {/* Branding */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-[var(--ps-text-muted)]">
            <Share2 className="w-4 h-4" />
            <span className="text-sm">Shared to Par Seeker</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareTargetPage;
